import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { NameEditor } from '../auth/NameEditor.tsx';
import type { HostedLobby } from '../match/useMatch.ts';

export interface HostPanelProps {
  readonly lobby: HostedLobby;
  readonly onCancel: () => void;
  /** The host's own name, shown to everyone who scans in. */
  readonly hostName: string;
  readonly onRename: (name: string) => void;
  readonly onMoveWaiter: (userId: string, direction: 'up' | 'down') => void;
  readonly onStartWith: (userId: string) => void;
  readonly onKickWaiter: (userId: string) => void;
}

/**
 * The waiting list, in the order the host arranged it.
 *
 * Reordering is one step at a time rather than drag-and-drop: this is operated
 * live, often on a second monitor or a phone, where a drag that lands in the
 * wrong slot costs more than the two clicks it saved.
 */
function Queue({
  waiting,
  onMoveWaiter,
  onStartWith,
  onKickWaiter,
}: Pick<HostPanelProps, 'onMoveWaiter' | 'onStartWith' | 'onKickWaiter'> & {
  waiting: HostedLobby['waiting'];
}) {
  return (
    <div className="host__queue">
      <h3 className="host__queue-heading" id="host-queue-heading">
        Who&rsquo;s waiting
      </h3>
      {/*
        Polite, not assertive: someone joining is worth announcing, but it must
        never cut across whatever the host is doing.
      */}
      <ul className="host__waiters" aria-labelledby="host-queue-heading" aria-live="polite">
        {waiting.length === 0 ? (
          // Honest empty state. A placeholder row here would read as a person.
          <li className="host__queue-empty">Nobody yet — the code is live.</li>
        ) : (
          waiting.map((waiter, index) => (
            <li className="host__waiter" key={waiter.userId}>
              <span className="host__waiter-rank" aria-hidden="true">
                {index + 1}
              </span>
              <span className="host__waiter-who">
                <span className="host__waiter-name">{waiter.displayName}</span>
                <span className="host__waiter-rating">rated {waiter.rating}</span>
              </span>
              <span className="host__waiter-order">
                <button
                  type="button"
                  className="button button--ghost host__nudge"
                  onClick={() => onMoveWaiter(waiter.userId, 'up')}
                  disabled={index === 0}
                  aria-label={`Move ${waiter.displayName} up`}
                >
                  <span aria-hidden="true">▲</span>
                </button>
                <button
                  type="button"
                  className="button button--ghost host__nudge"
                  onClick={() => onMoveWaiter(waiter.userId, 'down')}
                  disabled={index === waiting.length - 1}
                  aria-label={`Move ${waiter.displayName} down`}
                >
                  <span aria-hidden="true">▼</span>
                </button>
              </span>
              <button
                type="button"
                className="button button--primary"
                onClick={() => onStartWith(waiter.userId)}
                aria-label={`Play ${waiter.displayName} next`}
              >
                Play
              </button>
              <button
                type="button"
                className="button button--quiet"
                onClick={() => onKickWaiter(waiter.userId)}
                aria-label={`Remove ${waiter.displayName} from the queue`}
              >
                Kick
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

/**
 * What the streamer puts on screen, and the queue they run it from.
 *
 * Two ways in, because a stream audience is split between phones and desktops:
 * a QR to scan, and a code short enough to read aloud. Both resolve to the same
 * server-authored `joinPath`, so they cannot drift apart.
 *
 * The QR is rendered to a data URL rather than an <img src> pointing at a
 * generator service: it must work with no third-party request, and the join URL
 * should never leave the browser.
 */
export function HostPanel({
  lobby,
  onCancel,
  hostName,
  onRename,
  onMoveWaiter,
  onStartWith,
  onKickWaiter,
}: HostPanelProps) {
  const joinUrl = new URL(lobby.joinPath, window.location.origin).toString();
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let live = true;
    QRCode.toDataURL(joinUrl, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 320,
      // Fixed black on white: a QR must stay scannable regardless of theme, and
      // camera apps cope badly with low-contrast or inverted codes.
      color: { dark: '#000000ff', light: '#ffffffff' },
    })
      .then((url) => {
        if (live) setQr(url);
      })
      .catch(() => {
        // The code and link below still work, so a QR failure is not fatal.
        if (live) setQr(null);
      });
    return () => {
      live = false;
    };
  }, [joinUrl]);

  useEffect(
    () => () => {
      if (copyTimer.current !== null) clearTimeout(copyTimer.current);
    },
    [],
  );

  const copyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      if (copyTimer.current !== null) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="host" aria-labelledby="host-heading">
      <h2 id="host-heading" className="host__heading">
        Your game is open
      </h2>
      <p className="host__lede">Scan to join, or type the code at {window.location.host}</p>

      {/* Editable here, not only back on the idle screen: this is the name every
          waiting player is looking at while they decide to stay. */}
      <NameEditor currentName={hostName} onRename={onRename} />

      {/* QR and code share a row so the queue below stays above the fold on a
          laptop, which is where the host actually works. */}
      <div className="host__share">
        <div className="host__qr">
          {qr ? (
            <img src={qr} alt={`QR code linking to ${joinUrl}`} width={240} height={240} />
          ) : (
            <div className="host__qr-fallback">Use the code below to join.</div>
          )}
        </div>

        <div className="host__share-detail">
          {/*
            Letter-spaced and monospaced so it survives being read aloud, filmed
            at an angle, and re-typed on a phone.
          */}
          <p className="host__code" aria-label={`Game code ${lobby.code.split('').join(' ')}`}>
            {lobby.code}
          </p>

          <div className="host__actions">
            <button type="button" className="button button--ghost" onClick={() => void copyLink()}>
              {copied ? 'Link copied' : 'Copy link'}
            </button>
            <button type="button" className="button button--ghost" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </div>
      </div>

      <Queue
        waiting={lobby.waiting}
        onMoveWaiter={onMoveWaiter}
        onStartWith={onStartWith}
        onKickWaiter={onKickWaiter}
      />
    </section>
  );
}
